/**
 * OauthPostmessageController — OAuth 인증 결과 postMessage 전송 컨트롤러
 *
 * 팝업 창에서 OAuth 인증 완료 후 부모 창(게임 프론트)으로 결과를 postMessage로 전송
 * 그 후 100ms 후 팝업 창을 자동으로 닫음
 *
 * postMessage 포맷:
 *   {
 *     from: 'webxcom',
 *     type: 'success' | 'fail',
 *     data: JWT_TOKEN | ERROR_MESSAGE
 *   }
 *
 * 값 (Values):
 *   jwt      — JWT 토큰 (String)
 *   origin   — 부모 창의 origin (String, "*" 금지)
 *   type     — 메시지 타입 (String, 기본값: 'success')
 *   autoSend — true: connect() 시 즉시 전송 (authorize 화면)
 *              false: 버튼 클릭 시 confirmAndSend() 호출 대기 (consent 화면)
 *
 * 사용법 — 자동 전송 (authorize.html.erb):
 *   <div data-controller="oauth-postmessage"
 *        data-oauth-postmessage-jwt-value="<%= @jwt_token %>"
 *        data-oauth-postmessage-origin-value="<%= @origin %>"
 *        data-oauth-postmessage-type-value="success"
 *        data-oauth-postmessage-auto-send-value="true">
 *   </div>
 *
 * 사용법 — 수동 전송 (consent.html.erb, 동의 버튼 클릭 시):
 *   <div data-controller="oauth-postmessage"
 *        data-oauth-postmessage-jwt-value="<%= @jwt_token %>"
 *        data-oauth-postmessage-origin-value="<%= @origin %>"
 *        data-oauth-postmessage-type-value="success"
 *        data-oauth-postmessage-auto-send-value="false">
 *     <button data-action="click->oauth-postmessage#confirmAndSend">동의하고 시작하기</button>
 *   </div>
 */
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // 값 정의: JWT 토큰, 부모 창 origin, 메시지 타입, 자동 전송 여부
  static values = {
    jwt:      String,
    origin:   String,
    type:     { type: String,   default: "success" },
    // autoSend: true → authorize 화면에서 로드 즉시 전송
    //           false → consent 화면에서 버튼 클릭 후 전송
    autoSend: { type: Boolean, default: true }
  }

  // ── 초기화 ──────────────────────────────────────────────
  // autoSend가 true인 경우에만 connect() 시점에 즉시 전송
  // consent 화면(autoSend=false)에서는 버튼 클릭을 기다림
  connect() {
    if (this.autoSendValue) {
      this.sendResult()
    }
  }

  // ── 동의 버튼 클릭 핸들러 (consent 화면 전용) ───────────
  // consent.html.erb 의 동의 버튼에서 data-action="click->oauth-postmessage#confirmAndSend" 로 호출
  confirmAndSend() {
    this.sendResult()
  }

  // ── postMessage 전송 및 팝업 닫기 ────────────────────────
  sendResult() {
    // window.opener 확인: 팝업이 부모 창에서 열렸는지 검증
    if (!window.opener || window.opener.closed) {
      // 부모 창이 없거나 닫혀있으면 에러 메시지 표시
      document.body.innerHTML = '<p style="color:#FF2E63; font-family: JetBrains Mono; padding: 20px;">이 페이지는 앱에서 열어야 합니다.</p>'
      return
    }

    // origin 검증: 빈 문자열이면 postMessage가 SyntaxError를 던짐
    if (!this.originValue) {
      document.body.innerHTML = '<p style="color:#FF2E63; font-family: JetBrains Mono; padding: 20px;">잘못된 origin 설정입니다. 앱 관리자에게 문의하세요.</p>'
      return
    }

    // postMessage 포맷: { from: 'webxcom', type, data }
    const message = {
      from: "webxcom",
      type: this.typeValue,
      data: this.jwtValue
    }

    // postMessage 전송 — targetOrigin은 반드시 구체적 origin 사용 ('*' 금지)
    // origin은 서버에서 redirect_uri 또는 referer에서 추출하여 전달됨
    window.opener.postMessage(message, this.originValue)

    // 100ms 후 팝업 창 자동 닫기
    setTimeout(() => {
      window.close()
    }, 100)
  }
}
